import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, XCircle, Trophy } from 'lucide-react';
import { LogoFull } from '../components/Logo';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [message, setMessage] = useState('Vérification du paiement...');
  const [attempts, setAttempts] = useState(0);
  
  const MAX_ATTEMPTS = 10;
  const POLL_INTERVAL = 2000; // 2 seconds

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setMessage('Session de paiement invalide');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= MAX_ATTEMPTS) {
      setStatus('error');
      setMessage('Délai de vérification dépassé. Veuillez vérifier votre email pour confirmation.');
      return;
    }

    try {
      const token = localStorage.getItem('spirify_token');
      const response = await fetch(
        `${window.location.origin}/api/payments/checkout/status/${sessionId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de la vérification du paiement');
      }

      const data = await response.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        setMessage('Paiement réussi ! Vous êtes maintenant membre Premium 🎉');
      } else if (data.status === 'expired') {
        setStatus('error');
        setMessage('La session de paiement a expiré. Veuillez réessayer.');
      } else {
        // Still pending, continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, POLL_INTERVAL);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      setMessage('Erreur lors de la vérification du paiement. Veuillez contacter le support.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
            <LogoFull iconClassName="w-12 h-12" textClassName="text-3xl" />
          </button>
        </div>

        {/* Status Card */}
        <Card className="shadow-2xl border-0">
          <CardContent className="p-8">
            <div className="text-center">
              {status === 'checking' && (
                <>
                  <Loader2 className="w-20 h-20 text-purple-600 mx-auto mb-6 animate-spin" />
                  <h2 className="text-2xl font-bold mb-3">Vérification en cours...</h2>
                  <p className="text-gray-600 mb-6">{message}</p>
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </>
              )}

              {status === 'success' && (
                <>
                  <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-6" />
                  <h2 className="text-3xl font-bold text-green-600 mb-3">
                    Paiement réussi ! 🎉
                  </h2>
                  <p className="text-lg text-gray-700 mb-6">{message}</p>
                  
                  <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6 mb-6">
                    <Trophy className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                    <h3 className="font-bold text-lg mb-2">Bienvenue dans Spirify Premium !</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Vous avez maintenant accès à tous les parcours et à 160 leçons pour approfondir vos connaissances.
                    </p>
                    <ul className="text-sm text-left space-y-2 max-w-xs mx-auto">
                      <li className="flex items-center space-x-2">
                        <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0" />
                        <span>Accès illimité à tous les niveaux</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0" />
                        <span>160 leçons approfondies</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0" />
                        <span>Contenu premium exclusif</span>
                      </li>
                    </ul>
                  </div>

                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-6 text-lg font-semibold shadow-lg"
                  >
                    Commencer à apprendre
                  </Button>
                </>
              )}

              {status === 'error' && (
                <>
                  <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
                  <h2 className="text-2xl font-bold text-red-600 mb-3">
                    Problème rencontré
                  </h2>
                  <p className="text-gray-600 mb-6">{message}</p>
                  <div className="space-y-3">
                    <Button
                      onClick={() => navigate('/pricing')}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white py-4"
                    >
                      Réessayer
                    </Button>
                    <Button
                      onClick={() => navigate('/dashboard')}
                      variant="outline"
                      className="w-full border-purple-600 text-purple-600 hover:bg-purple-50 py-4"
                    >
                      Retour au dashboard
                    </Button>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {status === 'checking' && (
          <p className="text-center text-sm text-gray-500 mt-4">
            Ne fermez pas cette page pendant la vérification...
          </p>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
